import React from 'react';
import PropTypes from 'prop-types';

import noop from 'lodash/noop';
import get from 'lodash/get';
import without from 'lodash/without';

import { Checkbox } from 'antd';
import { CheckOutlined } from '@ant-design/icons';

import { MatchPropTypes } from '@models/Match';
import BracketTeam from '@views/BracketTeam';
import Team from '@views/Team';
import TeamSelector from '@views/TeamSelector';
import Score from '@views/Score';
import { getTemplateTeam } from '@views/BracketTeam';

const getSideProps = (
  side,
  { hasBothTeams, canEditScore, canEditTeams, teams, ...props }
) => {
  const team = get(props, `team${side}`);
  return {
    team,
    score: hasBothTeams ? get(props, `score${side}`) : noop(),
    from: get(props, `from${side}`),
    canEditScore,
    canEditTeam: canEditTeams,
    showCheckbox: hasBothTeams
      ? get(props, `scoreHome`, false) === get(props, `scoreAway`, true)
      : false,
    teams: get(teams, side.toLowerCase()),
    teamGroup: team
      ? get(props, ['teamGroupByCode', String(team).toLowerCase()])
      : undefined,
    isWinner: get(props, 'winner') === side.toLowerCase(),
    onChangeScore: (value) => {
      const otherSide = side === 'Home' ? 'Away' : 'Home';
      let winnerChange = {};
      if (hasBothTeams && get(props, `score${otherSide}`) !== value) {
        winnerChange = { winner: undefined };
      }
      props.onChange({ [`score${side}`]: value, ...winnerChange });
    },
    onChangeTeam: (value) => props.onChange({ [`team${side}`]: value }),
    onChangeWinner: (e) =>
      props.onChange({ winner: e.target.checked ? side.toLowerCase() : '' }),
  };
};

// Mapeo de assertions v2 a clases CSS v1
const mapAssertionsToCssClasses = (assertions = []) => {
  return assertions.map(assertion => {
    // Sistema v2 -> Sistema v1 (CSS)
    if (assertion === 'resultado-correcto') return 'groups-winner';
    if (assertion === 'resultado-exacto') return 'groups-winner groups-score';
    if (assertion === 'equipo-clasificado') return 'bracket-winner';
    if (assertion === 'resultado-exacto-eliminatoria') return 'bracket-winner bracket-score';
    // Sistema v1 (mantener como está)
    return assertion;
  }).join(' ');
};

// Determina el ganador basándose en scores o el campo winner
const getMatchWinner = (match) => {
  if (!match) return '';
  
  // Si hay winner explícito (penales), usarlo
  if (match.winner === 'home' || match.winner === 'away') {
    return match.winner;
  }
  
  // Si hay scores, calcular ganador
  const scoreHome = match.scoreHome;
  const scoreAway = match.scoreAway;
  
  if (scoreHome != null && scoreAway != null && scoreHome !== scoreAway) {
    return scoreHome > scoreAway ? 'home' : 'away';
  }
  
  return '';
};

export const getMatchClass = (match) => {
  const winner = getMatchWinner(match);
  return `${match.isToday ? 'match-today' : ''} ${mapAssertionsToCssClasses(without(
    get(match, 'assertions', []),
    'champion'
  ))} ${winner ? `winner-${winner}` : ''}`.trim();
};

const ParrillaPlaceholderHome = () => (
  <span className="bracket-parrilla-placeholder bracket-parrilla-placeholder--home">
    <span className="bracket-parrilla-placeholder-name">Selección</span>
    <span className="bracket-team-placeholder-flag" aria-hidden />
  </span>
);

const ParrillaPlaceholderAway = () => (
  <span className="bracket-parrilla-placeholder bracket-parrilla-placeholder--away">
    <span className="bracket-team-placeholder-flag" aria-hidden />
    <span className="bracket-parrilla-placeholder-name">Selección</span>
  </span>
);

const ParrillaHomePick = ({ canEditTeam, team, teams, onChangeTeam }) =>
  canEditTeam ? (
    <TeamSelector
      selected={team}
      teams={teams || []}
      onChange={onChangeTeam}
      placeholder={<ParrillaPlaceholderHome />}
    />
  ) : typeof team === 'string' && team ? (
    <Team code={team} align="right" />
  ) : (
    <ParrillaPlaceholderHome />
  );

const ParrillaAwayPick = ({ canEditTeam, team, teams, onChangeTeam }) =>
  canEditTeam ? (
    <TeamSelector
      selected={team}
      teams={teams || []}
      onChange={onChangeTeam}
      placeholder={<ParrillaPlaceholderAway />}
    />
  ) : typeof team === 'string' && team ? (
    <Team code={team} align="left" />
  ) : (
    <ParrillaPlaceholderAway />
  );

const ParrillaScoreBox = ({ canEditScore, value, onChangeScore }) =>
  canEditScore ? (
    <Score value={value} onChange={onChangeScore} placeholder="–" />
  ) : typeof value === 'number' && !Number.isNaN(value) ? (
    <span className="match-parrilla-score-readonly">{value}</span>
  ) : (
    <span className="bracket-score-placeholder bracket-score-placeholder--parrilla">
      —
    </span>
  );

ParrillaHomePick.propTypes = {
  canEditTeam: PropTypes.bool,
  team: PropTypes.string,
  teams: PropTypes.arrayOf(PropTypes.string),
  onChangeTeam: PropTypes.func,
};

ParrillaAwayPick.propTypes = ParrillaHomePick.propTypes;

const ParrillaSeed = ({
  seed,
  align,
  showTiebreak,
  canEditScore,
  isWinner,
  checked,
  onChangeWinner,
}) =>
  seed ? (
    <span
      className={`match-parrilla-seed${
        align === 'away' ? ' match-parrilla-seed--away' : ''
      }${showTiebreak ? ' match-parrilla-seed--tiebreak' : ''}`}
    >
      {showTiebreak ? (
        canEditScore ? (
          <Checkbox
            className="match-parrilla-seed-tiebreak"
            checked={checked}
            onChange={onChangeWinner}
          />
        ) : isWinner ? (
          <CheckOutlined className="match-parrilla-seed-winner" />
        ) : (
          <span className="match-parrilla-seed-tiebreak-spacer" aria-hidden />
        )
      ) : null}
      <b className="from">{seed}</b>
    </span>
  ) : null;

const BracketMatchParrilla = ({ round, ...props }) => {
  const homeProps = getSideProps('Home', props);
  const awayProps = getSideProps('Away', props);
  const seed = getTemplateTeam(props.fromHome, homeProps.teamGroup);
  const seedAway = getTemplateTeam(props.fromAway, awayProps.teamGroup);
  const showTiebreak = props.hasBothTeams && homeProps.showCheckbox;

  return (
    <div className={`match match--parrilla ${round} ${getMatchClass(props)}`}>
      <div
        className={`participant participant--parrilla-row ${
          props.canEditScore ? 'editable' : ''
        } ${props.canEditTeams ? 'selectable' : ''}`}
      >
        <div
          className={`match-parrilla-home${
            seed ? '' : ' match-parrilla-home--no-seed'
          }`}
        >
          <ParrillaSeed
            seed={seed}
            showTiebreak={showTiebreak}
            canEditScore={props.canEditScore}
            isWinner={homeProps.isWinner}
            checked={props.winner === 'home'}
            onChangeWinner={homeProps.onChangeWinner}
          />
          <div className="match-parrilla-team-slot match-parrilla-team-slot--home">
            <ParrillaHomePick
              canEditTeam={homeProps.canEditTeam}
              team={homeProps.team}
              teams={homeProps.teams}
              onChangeTeam={homeProps.onChangeTeam}
            />
          </div>
        </div>
        <div className="match-parrilla-scores">
          <div className="match-parrilla-scores-inner">
            <ParrillaScoreBox
              canEditScore={homeProps.canEditScore}
              value={props.scoreHome}
              onChangeScore={homeProps.onChangeScore}
            />
            <ParrillaScoreBox
              canEditScore={awayProps.canEditScore}
              value={props.scoreAway}
              onChangeScore={awayProps.onChangeScore}
            />
          </div>
        </div>
        <div
          className={`match-parrilla-away${
            seedAway ? '' : ' match-parrilla-away--no-seed'
          }`}
        >
          <div className="match-parrilla-team-slot match-parrilla-team-slot--away">
            <ParrillaAwayPick
              canEditTeam={awayProps.canEditTeam}
              team={awayProps.team}
              teams={awayProps.teams}
              onChangeTeam={awayProps.onChangeTeam}
            />
          </div>
          <ParrillaSeed
            seed={seedAway}
            align="away"
            showTiebreak={showTiebreak}
            canEditScore={props.canEditScore}
            isWinner={awayProps.isWinner}
            checked={props.winner === 'away'}
            onChangeWinner={awayProps.onChangeWinner}
          />
        </div>
      </div>
    </div>
  );
};

const BracketMatch = ({ round, parrillaRow, ...props }) =>
  parrillaRow === true ? (
    <BracketMatchParrilla round={round} {...props} />
  ) : (
    <div className={`match ${round} ${getMatchClass(props)}`}>
      <div className="team team--home">
        <BracketTeam {...getSideProps('Home', props)} />
      </div>
      <div className="team team--away">
        <BracketTeam {...getSideProps('Away', props)} />
      </div>
    </div>
  );

BracketMatch.displayName = 'BracketMatch';
BracketMatch.propTypes = Object.assign({}, MatchPropTypes, {
  parrillaRow: PropTypes.bool,
  canEditScore: PropTypes.bool,
  canEditTeams: PropTypes.bool,
  hasBothTeams: PropTypes.bool,
  teams: PropTypes.shape({
    home: PropTypes.arrayOf(PropTypes.string),
    away: PropTypes.arrayOf(PropTypes.string),
  }),
  onChange: PropTypes.func,
});
BracketMatch.defaultProps = {
  parrillaRow: false,
  hasBothTeams: false,
  canEditScore: false,
  canEditTeams: false,
  onChange: noop,
};

export default BracketMatch;
